import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useI18n } from "../lib/i18n";
import {
  ChatCircleDots,
  X,
  PaperPlaneTilt,
  CircleNotch,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getOrCreateSessionId() {
  let id = localStorage.getItem("mk360_chat_session");
  if (!id) {
    id =
      "s-" +
      Date.now().toString(36) +
      "-" +
      Math.random().toString(36).slice(2, 8);
    localStorage.setItem("mk360_chat_session", id);
  }
  return id;
}

const COPY = {
  it: {
    title: "Jonny — Assistente Makelaars360",
    subtitle: "Online — risponde in pochi secondi",
    placeholder: "Scrivi un messaggio...",
    welcome:
      "Ciao, sono Jonny, l'assistente virtuale di Makelaars360. Posso aiutarti su servizi, immobili in vetrina e contatti. Cosa vuoi sapere?",
    send: "Invia",
    error: "Errore di connessione. Riprova.",
    suggestions: [
      "Quali servizi offrite?",
      "Come posso fissare una consulenza?",
      "Mi parli di investimenti immobiliari",
    ],
  },
  en: {
    title: "Jonny — Makelaars360 Assistant",
    subtitle: "Online — usually responds in seconds",
    placeholder: "Write a message...",
    welcome:
      "Hi, I'm Jonny, the virtual assistant of Makelaars360. I can help with services, showcased properties and contacts. How can I help?",
    send: "Send",
    error: "Connection error. Please retry.",
    suggestions: [
      "What services do you offer?",
      "How can I book a consultation?",
      "Tell me about real estate investments",
    ],
  },
};

export default function ChatBot() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.it;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionIdRef = useRef(getOrCreateSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: c.welcome }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionIdRef.current,
        message,
        lang,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      console.error(e);
      const friendly = e?.response?.data?.detail;
      setMessages((m) => [
        ...m,
        { role: "assistant", content: typeof friendly === "string" ? friendly : c.error },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating launcher button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="chatbot-toggle"
        aria-label={open ? "Chiudi chat" : "Apri chat"}
        className={`fixed z-[60] bottom-20 right-6 w-14 h-14 flex items-center justify-center bg-[#0A1428] text-[#C5A059] border border-[#C5A059]/40 shadow-2xl hover:bg-[#C5A059] hover:text-[#0A1428] transition-all duration-300 ${
          open ? "scale-90" : "hover:-translate-y-1"
        }`}
      >
        {open ? (
          <X size={22} weight="bold" />
        ) : (
          <ChatCircleDots size={24} weight="duotone" />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed z-[59] bottom-[9.5rem] right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[400px] h-[560px] max-h-[calc(100vh-11rem)] bg-white border border-[#0A1428]/15 shadow-[0_25px_60px_-15px_rgba(10,20,40,0.35)] flex flex-col"
        >
          {/* Header */}
          <div className="bg-[#0A1428] text-white px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C5A059] flex items-center justify-center">
              <ChatCircleDots size={18} weight="duotone" className="text-[#0A1428]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-base leading-tight">{c.title}</p>
              <p className="text-[10px] uppercase tracking-widest text-[#C5A059] mt-0.5 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full inline-block" />
                {c.subtitle}
              </p>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-[#F8F9FA]"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}`}
                className={`flex ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-line ${
                    m.role === "user"
                      ? "bg-[#0A1428] text-white"
                      : "bg-white text-[#0A1428] border border-[#0A1428]/10"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#0A1428]/10 px-4 py-3">
                  <CircleNotch size={16} className="animate-spin text-[#C5A059]" />
                </div>
              </div>
            )}

            {/* Suggestions only when 1 message (welcome) */}
            {messages.length === 1 && !loading && (
              <div className="space-y-2 pt-2">
                {c.suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="chat-suggestion"
                    className="block w-full text-left px-3 py-2 text-xs border border-[#0A1428]/15 bg-white hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-[#0A1428]/10 px-3 py-3 bg-white flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder={c.placeholder}
              disabled={loading}
              data-testid="chat-input"
              className="flex-1 resize-none bg-transparent border border-[#0A1428]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059] max-h-24"
              style={{ minHeight: "40px" }}
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              data-testid="chat-send-button"
              aria-label={c.send}
              className="w-10 h-10 flex items-center justify-center bg-[#C5A059] text-[#0A1428] hover:bg-[#b08d4f] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <PaperPlaneTilt size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
